import { Pill, PrivateRoomGallery } from '@design-system';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';

import FooterLocale from '../../../../components/Layout/Footer/FooterLocale';
import { ProductGateway } from '../../../../types/AvantArteProduct';
import { GeneratedPrivateLink } from '../../../../types/generated';
import { GiftCardCheckoutPayload } from '../../../../types/GiftCard';
import {
  CheckoutGateway,
  ProductsGateway,
  ShopifyProductPurchaseDetails,
} from '../../../../types/Purchasing';
import { AnalyticsObjectProvider } from '../../../../utils/analytics/object';
import { renderArtistsNamesFromPageContent } from '../../../../utils/artists';
import {
  PrivateLinkProduct,
  PrivateSalesController,
} from '../../types/PrivateLink';
import { MoreWorks } from '../single-product/MoreWorks';
import { ProductSlide } from '../slides/ProductSlide';
import { RoomDateCountdown } from '../unlock/RoomCountDown';
import { VideoPreview } from '../video/VideoPreview';

type Props = {
  privateLink: GeneratedPrivateLink;
  privateSalesController: PrivateSalesController;
  checkoutGateway: CheckoutGateway<
    ShopifyProductPurchaseDetails,
    GiftCardCheckoutPayload
  >;
  aaProductsGateway: ProductGateway;
  productsGateway: ProductsGateway<
    ShopifyProductIdentifier,
    ShopifyProductPurchaseDetails
  >;
};

export const List: React.FC<Props> = ({
  aaProductsGateway,
  checkoutGateway,
  privateLink,
  privateSalesController,
  productsGateway,
}) => {
  const { t } = useTranslation('private');
  const router = useRouter();
  const productIdQuery =
    typeof router.query.productId === 'string' ? router.query.productId : null;
  const { expiresAt, heading } = privateLink;
  const { products } = privateSalesController;
  const [product, setProduct] = useState<PrivateLinkProduct>();
  const [view, setView] = useState<'grid' | 'carousel'>(
    products.length > 4 ? 'grid' : 'carousel',
  );

  return (
    <AnalyticsObjectProvider
      name="product list"
      properties={{
        'private_link_slug': privateLink.slug,
        'product_title': product?.shopifyProduct.title,
        'product_artist': renderArtistsNamesFromPageContent(
          (product?.storyblokProduct.component === 'product_v2'
            ? product?.storyblokProduct.artists
            : product?.storyblokProduct
                .productArtist) as unknown as RelatedArtist[],
        ),
        'product_medium': product?.storyblokProduct.medium,
        'product_edition_size': product?.shopifyProduct.editionSize,
      }}
    >
      {() => (
        <div className="flex w-full flex-col items-center justify-center gap-24 py-9 lg:py-12">
          <div className="flex w-full flex-col items-center justify-center gap-9 lg:gap-12">
            <div className="flex w-full max-w-lg flex-col items-center gap-4 px-website text-center">
              <h2 className="w-full text-neutral-3">{t('previewLabel')}</h2>
              <h1 className="w-full text-md font-bold text-white lg:text-lg">
                {heading}
              </h1>
              {privateLink.intro && (
                <div className="w-full flex-1">
                  <p className="text-white">{privateLink.intro}</p>
                </div>
              )}
              {expiresAt && (
                <Pill
                  label={<RoomDateCountdown expiresAt={new Date(expiresAt)} />}
                />
              )}
            </div>

            {privateLink.videoUrl ? (
              <div className="relative w-full max-w-website px-website">
                <VideoPreview privateLink={privateLink} />
              </div>
            ) : null}

            <PrivateRoomGallery
              items={products.map((privateLinkProduct) => {
                return (
                  <ProductSlide
                    aaProductsGateway={aaProductsGateway}
                    autoOpen={
                      productIdQuery !== null &&
                      privateLinkProduct.shopifyProductIdentifier.id ===
                        productIdQuery
                    }
                    checkoutGateway={checkoutGateway}
                    privateLink={privateLink}
                    privateLinkProduct={privateLinkProduct}
                    privateLinkSlug={privateLink.slug}
                    productsGateway={productsGateway}
                    setShowDialog={() => {
                      setProduct(privateLinkProduct);
                    }}
                    showButtons={view === 'carousel'}
                    view={view}
                  />
                );
              })}
              onViewUpdate={(newValue: 'grid' | 'carousel') =>
                setView(newValue)
              }
              view={view}
            />

            <div className="flex w-full flex-col items-center justify-center px-website">
              <MoreWorks />
            </div>
          </div>

          <div className="flex w-full flex-col items-center justify-center px-website text-white">
            <FooterLocale />
          </div>
        </div>
      )}
    </AnalyticsObjectProvider>
  );
};
